import React, { useEffect, useState } from 'react';
import { ChevronDown, Trophy } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { PANEL, firstOf } from './DeskKit';
import { useScoreboard } from '../../../services/scoreboard';

// The Scoreboard card (Nic 2026-11-03, mockup option D): the whole team,
// today and this week, ranked by the week with a bar each, the viewer's
// row lit, the team's totals under it. Above the Today checklist in the
// rail. Fully collapsible; the fold is saved with the person (board prefs),
// so a refresh or a cleared cache keeps it. Folded, one line of numbers
// stays so it still says something.
const WORDS = {
  sales: { what: 'Agreements', unit: 'signup' },
  onboarding: { what: 'FOTCs', unit: 'FOTC' },
};

const ScoreboardCard = ({
  team = 'sales',
  enabled = true,
  folded = false,
  onFold,
}) => {
  const q = useScoreboard(team, enabled);
  const words = WORDS[team] || WORDS.sales;
  // The fold flips at once; the save follows (Nic 2026-10-12's Hide done).
  const [closed, setClosed] = useState(!!folded);
  useEffect(() => setClosed(!!folded), [folded]);
  if (!enabled || !q.data) return null;
  const people = q.data.people || [];
  const totals = q.data.totals || { today: 0, week: 0 };
  const max = Math.max(1, ...people.map((p) => p.week));
  const flip = () => {
    const next = !closed;
    setClosed(next);
    onFold?.(next);
  };
  const me = people.find((p) => p.me);
  const others = people.filter((p) => !p.me).slice(0, 2);
  const folds = [
    me ? `You ${me.today} today` : null,
    ...others.map((p) => `${firstOf(p.name)} ${p.today}`),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className={cn(PANEL, 'p-4')} aria-label="Scoreboard">
      <div className="flex items-center gap-2">
        <Trophy size={14} className="shrink-0 text-amber-500" />
        <h3 className="mb-0 text-[14px] font-bold text-slate-800 dark:text-slate-100">
          Scoreboard
        </h3>
        <span className="text-[11px] text-slate-400">{words.what}</span>
        <button
          type="button"
          onClick={flip}
          aria-expanded={!closed}
          aria-label={closed ? 'Show the scoreboard' : 'Fold the scoreboard'}
          className="ml-auto inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] font-semibold text-slate-500 outline-none hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-400 dark:hover:!bg-white/10 dark:hover:text-white"
        >
          <span className="hidden sm:inline">Today · Week</span>
          <ChevronDown
            size={13}
            className={cn('transition-transform', closed && '-rotate-90')}
          />
        </button>
      </div>
      {closed ? (
        <p className="mb-0 mt-1.5 truncate text-[12px] text-slate-500 dark:text-slate-400">
          {folds || `No ${words.unit}s yet this week.`}
        </p>
      ) : (
        <>
          <ol className="mb-0 mt-3 flex list-none flex-col gap-2 pl-0">
            {people.map((p, i) => (
              <li
                key={p.userId}
                // The viewer's row: bold name and the violet bar, no fill (Nic 2026-11-03).
                className="rounded-lg px-2 py-1.5"
                aria-current={p.me ? 'true' : undefined}
              >
                <div className="flex items-center gap-2 text-[12.5px]">
                  <span className="w-4 shrink-0 text-right text-[11px] tabular-nums text-slate-400">
                    {i + 1}
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate',
                      p.me
                        ? 'font-bold text-slate-900 dark:text-white'
                        : 'font-medium text-slate-700 dark:text-slate-200',
                    )}
                  >
                    {p.name}
                    {p.me ? (
                      <span className="ml-1 text-[10.5px] font-semibold text-violet-600 dark:text-violet-300">
                        (you)
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="w-6 shrink-0 text-right text-[13px] font-bold tabular-nums text-slate-900 dark:text-white"
                    title="Today"
                  >
                    {p.today}
                  </span>
                  <span
                    className="w-7 shrink-0 text-right text-[12px] tabular-nums text-slate-400"
                    title="This week"
                  >
                    {p.week}
                  </span>
                </div>
                <div className="ml-6 mt-1 h-[5px] overflow-hidden rounded-full bg-slate-100 dark:!bg-white/10">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width] duration-500',
                      p.me ? 'bg-violet-500' : 'bg-slate-300 dark:bg-white/30',
                    )}
                    style={{ width: `${Math.round((p.week / max) * 100)}%` }}
                  />
                </div>
              </li>
            ))}
            {people.length ? null : (
              <li className="px-2 py-1.5 text-[12.5px] text-slate-400">
                Nobody on the team yet.
              </li>
            )}
          </ol>
          <div className="mt-2.5 text-[11px] text-slate-400">
            Team{' '}
            <b className="font-semibold tabular-nums text-slate-600 dark:text-slate-300">
              {totals.today}
            </b>{' '}
            today ·{' '}
            <b className="font-semibold tabular-nums text-slate-600 dark:text-slate-300">
              {totals.week}
            </b>{' '}
            this week
          </div>
        </>
      )}
    </section>
  );
};

export default ScoreboardCard;
